import type { ReactNode } from 'react';
import type { PublicPlayer } from '@sincronia/shared';
import { PlayerAvatar } from '../Brand.js';
import { cn } from '../../lib/ui.js';
import { useT } from '../../i18n/index.js';

/**
 * Chips compactos: com 8 jogadores o conjunto ocupa no máximo duas linhas e
 * nunca esconde o centro do tabuleiro.
 */
export function PlayerChips({
  players,
  meId,
  showReady,
}: {
  players: PublicPlayer[];
  meId: string | null;
  showReady: boolean;
}): ReactNode {
  const t = useT();

  return (
    <ul
      className="safe-x flex flex-wrap items-center justify-center gap-1.5 px-3 pb-2"
      data-testid="player-chips"
    >
      {players.map((player) => {
        const isMe = player.id === meId;
        return (
          <li
            key={player.id}
            data-testid={`player-chip-${player.seat}`}
            className={cn(
              'flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs transition',
              isMe ? 'border-pulse-500/60 bg-night-800' : 'border-night-700 bg-night-900/70',
              !player.connected && 'opacity-50',
            )}
          >
            <PlayerAvatar
              avatarKey={player.avatarKey}
              name={player.name}
              size={20}
              dimmed={!player.connected}
            />
            <span className="max-w-20 truncate font-medium text-night-200">{player.name}</span>
            <span
              className="tabular rounded-full bg-night-950/70 px-1.5 text-[11px] text-night-300"
              aria-label={
                player.remainingCards === 1
                  ? t('game.cardsOne')
                  : player.remainingCards === 0
                    ? t('game.cardsNone')
                    : t('game.cards', { count: player.remainingCards })
              }
            >
              {player.remainingCards}
            </span>
            {showReady && player.ready ? (
              <span
                className="text-pulse-400"
                aria-label={t('ready.action')}
                title={t('ready.action')}
              >
                ✓
              </span>
            ) : null}
            {!player.connected ? (
              <span
                className="text-life"
                aria-label={t('common.offline')}
                title={t('common.offline')}
              >
                ⚠
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
